/**
 * @file IntegrationUsagePanel.tsx
 * @description Дневная статистика запросов к интеграции (phase12.0.1).
 *
 * Показывает:
 *  - сегодняшний счётчик + progress-bar к лимиту квоты (если задан в config.quotaDailyLimit)
 *  - разрез по source (party / address / bank)
 *  - bar-график 30 дней (компактный SVG, без библиотек)
 *
 * Только в режиме edit (когда у item есть id). На «новой» интеграции статистики нет.
 */

import { useEffect, useMemo, useState } from "react";

import { Alert } from "../ui/alert";
import { Skeleton } from "../ui/skeleton";
import { ApiError } from "../../lib/api-client";
import {
  INTEGRATION_KIND_META,
  platformIntegrations,
  type IntegrationUsageDayDto,
  type PlatformIntegrationDto,
} from "../../lib/api-platform-integrations";

interface IntegrationUsagePanelProps {
  integration: PlatformIntegrationDto;
  days?: number;
}

export function IntegrationUsagePanel({ integration, days = 30 }: IntegrationUsagePanelProps) {
  const [rows, setRows] = useState<IntegrationUsageDayDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    platformIntegrations
      .usage(integration.id, days)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить статистику");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [integration.id, days]);

  // Агрегация по дням и по source.
  const { byDate, bySource, totalToday, quotaDailyLimit } = useMemo(
    () => aggregate(rows, integration.config),
    [rows, integration.config],
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  const quotaPercent = quotaDailyLimit ? Math.min(100, Math.round((totalToday / quotaDailyLimit) * 100)) : null;
  const hasQuotaField =
    INTEGRATION_KIND_META[integration.kind].configFields?.some((f) => f.key === "quotaDailyLimit") ?? false;
  const quotaColor = quotaPercent === null
    ? "bg-on-surface/30"
    : quotaPercent < 60
      ? "bg-primary"
      : quotaPercent < 90
        ? "bg-warning"
        : "bg-destructive";

  return (
    <div className="flex flex-col gap-4">
      {/* Сегодняшний счётчик */}
      <div>
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="text-sm text-on-surface-variant">Сегодня</span>
          <span className="text-base font-medium">
            {totalToday.toLocaleString("ru-RU")}
            {quotaDailyLimit ? (
              <span className="text-on-surface-muted">
                {" / "}
                {quotaDailyLimit.toLocaleString("ru-RU")}
              </span>
            ) : null}
          </span>
        </div>
        {quotaPercent !== null ? (
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-on-surface/8">
            <div className={`h-full ${quotaColor}`} style={{ width: `${quotaPercent}%` }} />
          </div>
        ) : hasQuotaField ? (
          // Совет — только для видов, у которых в форме есть поле лимита.
          <p className="text-xs text-on-surface-muted mt-1">
            Дневной лимит не задан — укажите его в настройках интеграции, чтобы видеть, сколько запросов осталось.
          </p>
        ) : null}
      </div>

      {/* График 30 дней */}
      {byDate.length > 0 ? (
        <UsageBars data={byDate} quotaDailyLimit={quotaDailyLimit} />
      ) : (
        <p className="text-sm text-on-surface-muted italic">
          За последние {days} дней реальных запросов не было (всё бралось из кэша или интеграцию не использовали).
        </p>
      )}

      {/* Разрез по source */}
      {bySource.length > 0 ? (
        <div className="flex flex-col gap-1">
          <div className="text-xs font-semibold text-on-surface-variant">
            По типу запроса (за {days} дн.)
          </div>
          <ul className="flex flex-col gap-0.5">
            {bySource.map((s) => (
              <li
                key={s.source}
                className="flex items-baseline justify-between text-sm gap-2"
              >
                <span className="font-mono text-xs text-on-surface-variant">{s.source}</span>
                <span>
                  {s.count.toLocaleString("ru-RU")}
                  {s.errorCount > 0 ? (
                    <span className="text-destructive">
                      {" "}({s.errorCount} с ошибкой)
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

interface ByDate {
  date: string;
  count: number;
  errorCount: number;
}

interface BySource {
  source: string;
  count: number;
  errorCount: number;
}

function aggregate(
  rows: IntegrationUsageDayDto[],
  config: Record<string, unknown> | null,
): {
  byDate: ByDate[];
  bySource: BySource[];
  totalToday: number;
  quotaDailyLimit: number | null;
} {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const todayStr = today.toISOString().slice(0, 10);

  const byDateMap = new Map<string, ByDate>();
  const bySourceMap = new Map<string, BySource>();
  let totalToday = 0;

  for (const r of rows) {
    const d = byDateMap.get(r.date) ?? { date: r.date, count: 0, errorCount: 0 };
    d.count += r.count;
    d.errorCount += r.errorCount;
    byDateMap.set(r.date, d);

    const s = bySourceMap.get(r.source) ?? { source: r.source, count: 0, errorCount: 0 };
    s.count += r.count;
    s.errorCount += r.errorCount;
    bySourceMap.set(r.source, s);

    if (r.date === todayStr) totalToday += r.count;
  }

  const quotaRaw = config && typeof config === "object" ? (config as Record<string, unknown>).quotaDailyLimit : null;
  const quotaDailyLimit =
    typeof quotaRaw === "number" && quotaRaw > 0 ? quotaRaw : null;

  return {
    byDate: Array.from(byDateMap.values()).sort((a, b) => a.date.localeCompare(b.date)),
    bySource: Array.from(bySourceMap.values()).sort((a, b) => b.count - a.count),
    totalToday,
    quotaDailyLimit,
  };
}

/** Компактный bar-график 30 дней (SVG, без библиотек). */
function UsageBars({ data, quotaDailyLimit }: { data: ByDate[]; quotaDailyLimit: number | null }) {
  const max = Math.max(quotaDailyLimit ?? 0, ...data.map((d) => d.count), 1);
  const W = 320;
  const H = 60;
  const barW = W / data.length;

  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-semibold text-on-surface-variant">
        Запросов в день
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-16"
        preserveAspectRatio="none"
        role="img"
        aria-label="График запросов по дням"
      >
        {data.map((d, i) => {
          const h = (d.count / max) * (H - 2);
          const x = i * barW;
          const errH = (d.errorCount / max) * (H - 2);
          return (
            <g key={d.date}>
              <title>{`${d.date}: ${d.count}${d.errorCount ? ` (${d.errorCount} с ошибкой)` : ""}`}</title>
              <rect
                x={x + 0.5}
                y={H - h}
                width={barW - 1}
                height={h}
                fill="var(--color-primary)"
                opacity={0.85}
              />
              {errH > 0 ? (
                <rect
                  x={x + 0.5}
                  y={H - errH}
                  width={barW - 1}
                  height={errH}
                  fill="var(--color-destructive)"
                />
              ) : null}
            </g>
          );
        })}
        {quotaDailyLimit ? (
          <line
            x1={0}
            x2={W}
            y1={H - (quotaDailyLimit / max) * (H - 2)}
            y2={H - (quotaDailyLimit / max) * (H - 2)}
            stroke="var(--color-on-surface)"
            strokeWidth={0.6}
            strokeDasharray="2 2"
            opacity={0.45}
          />
        ) : null}
      </svg>
      <div className="flex justify-between text-xs text-on-surface-variant">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}
